/**
 * Shared create/edit article form for admin.
 */

import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import CustomModal from "../../shared/customModal/customModal";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import {
  deleteArticleCover,
  isValidArticleImageUrl,
  uploadArticleImage,
} from "../../../utils/uploadArticleImage";
import {
  Button,
  Div,
  FeedbackFormInput,
  FeedbackFormTextarea,
  H1,
  P,
} from "../../../styles/styles";
import {
  deleteArticle,
  saveArticle,
  selectArticleDeleting,
  selectArticleSaving,
} from "../../../features/articles/articlesSlice";
import { pickLang } from "../../../utils/i18nDisplay";
import AdminArticleCardsSection from "./adminArticleCardsSection";
import AdminArticleSiteSection from "./adminArticleSiteSection";
import {
  emptySiteForm,
  sitePublishError,
} from "../../../utils/siteArticle";

const LANGUAGES = ["en", "ru", "sr"];

export const ARTICLE_CREATE_FORM_ID = "article-create-form";

export function articleEditFormId(articleId) {
  return `article-edit-form-${articleId}`;
}

export default function AdminArticleFormPanel({
  title,
  formId,
  formData,
  setFormData,
  articleFirestoreKey,
  articleId,
  onCancel,
  onSaved,
  onDeleted,
  articleCategories,
  articlePetTags,
}) {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const saving = useSelector(selectArticleSaving);
  const deleting = useSelector(selectArticleDeleting);
  const isEdit = Boolean(articleId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [siteReady, setSiteReady] = useState(!isEdit);
  const [coverUploadError, setCoverUploadError] = useState("");
  const busy = saving || deleting;

  const categoryLabel = (row) =>
    pickLang(row.name || {}, "en") || row.id || "";

  const coverUploadDisabled = !articleFirestoreKey;

  const handleFieldChange = (field, lang, value) => {
    if (field === "coverUrl" || field === "publishedAt") {
      setFormData((prev) => ({ ...prev, [field]: value }));
    } else if (typeof lang === "string") {
      setFormData((prev) => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value },
      }));
    }
  };

  const toggleCat = (cid) =>
    setFormData((prev) => {
      const has = prev.categoryIds.includes(cid);
      return {
        ...prev,
        categoryIds: has
          ? prev.categoryIds.filter((x) => x !== cid)
          : [...prev.categoryIds, cid],
      };
    });

  const togglePet = (pid) =>
    setFormData((prev) => {
      const has = prev.petTagIds.includes(pid);
      return {
        ...prev,
        petTagIds: has
          ? prev.petTagIds.filter((x) => x !== pid)
          : [...prev.petTagIds, pid],
      };
    });

  const setChannel = (channel, checked) => {
    setFormData((prev) => {
      const mobile = channel === "mobile" ? checked : !prev.webOnly;
      const webApp = channel === "webApp" ? checked : !prev.mobileOnly;
      const site = channel === "site" ? checked : Boolean(prev.showOnSite);
      return {
        ...prev,
        webOnly: !mobile,
        mobileOnly: !webApp,
        showOnSite: site,
      };
    });
  };

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !articleFirestoreKey) return;
    setCoverUploading(true);
    setCoverUploadError("");
    try {
      const url = await uploadArticleImage({
        articleId: articleFirestoreKey,
        kind: "cover",
        file,
      });
      setFormData((p) => ({ ...p, coverUrl: url }));
    } catch (err) {
      setCoverUploadError(err?.message || "Cover upload failed");
    } finally {
      setCoverUploading(false);
    }
  };

  const onCoverClear = async () => {
    if (!articleFirestoreKey) {
      setFormData((p) => ({ ...p, coverUrl: "" }));
      return;
    }
    try {
      await deleteArticleCover(articleFirestoreKey);
      setFormData((p) => ({ ...p, coverUrl: "" }));
    } catch (err) {
      window.alert(err?.message || "Failed to remove cover");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!siteReady) {
      window.alert("The site article is still loading. Wait, then save.");
      return;
    }
    if (!articleFirestoreKey) {
      window.alert("Firestore is not initialized.");
      return;
    }

    const coverTrimmed = String(formData.coverUrl || "").trim();
    if (coverTrimmed && !isValidArticleImageUrl(coverTrimmed)) {
      window.alert(
        "Cover URL must be a full HTTPS link (e.g. firebasestorage.googleapis.com/...), not a filename.",
      );
      return;
    }

    const cardsRaw = formData.cards || [];
    for (let i = 0; i < cardsRaw.length; i += 1) {
      const img = String(cardsRaw[i]?.imageUrl || "").trim();
      if (img && !isValidArticleImageUrl(img)) {
        window.alert(
          `Slide ${i + 1} image URL must be a full HTTPS link, not a filename.`,
        );
        return;
      }
    }

    const seen = new Set();
    const sanitizedCards = cardsRaw.map((card) => {
      let id =
        typeof card?.id === "string" && String(card.id).trim()
          ? String(card.id).trim()
          : uuidv4();
      while (seen.has(id)) id = uuidv4();
      seen.add(id);
      return { ...card, id };
    });

    const site = { ...emptySiteForm(), ...(formData.site || {}) };
    const ogTrimmed = String(site.ogImageUrl || "").trim();
    const publishError = sitePublishError({
      showOnSite: Boolean(formData.showOnSite),
      slugEn: formData.slug?.en,
      site,
      ogInvalid: Boolean(ogTrimmed) && !isValidArticleImageUrl(ogTrimmed),
    });
    if (publishError) {
      window.alert(publishError);
      return;
    }

    const data = {
      slug: formData.slug,
      title: formData.title,
      subtitle: formData.subtitle,
      coverUrl: formData.coverUrl,
      categoryIds: formData.categoryIds,
      petTagIds: formData.petTagIds,
      cards: sanitizedCards.length > 0 ? sanitizedCards : [],
      publishedAt: dayjs(formData.publishedAt).toDate(),
      webOnly: Boolean(formData.webOnly),
      mobileOnly: Boolean(formData.mobileOnly),
      showOnSite: Boolean(formData.showOnSite),
      pinned: Boolean(formData.pinned),
      site,
    };

    const result = await dispatch(
      saveArticle({ id: articleFirestoreKey, data }),
    );
    if (result.meta.requestStatus === "fulfilled") {
      onSaved();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!articleId) return;
    const result = await dispatch(deleteArticle(articleId));
    if (result.meta.requestStatus === "fulfilled") {
      setConfirmDeleteOpen(false);
      onDeleted?.();
    }
  };

  return (
    <Div
      width="100%"
      padding="20px"
      borderRadius="10px"
      backWhite
      grayBorder
      style={{ marginTop: 12, boxSizing: "border-box" }}
    >
      <H1 fontSize="20px" margin="0 0 15px 0">
        {title}
      </H1>

      <form id={formId} onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Div gap="15px" width="100%">
          <Div row gap="15px" width="100%" justifySpace>
            <Div flex="1" alignStart width="60%" justifyStart gap="10px">
              <P textWidth="auto" fontWeight="600">
                Cover image
              </P>
              {formData.coverUrl ? (
                <img
                  alt=""
                  src={formData.coverUrl}
                  style={{
                    maxWidth: 220,
                    maxHeight: 140,
                    objectFit: "contain",
                    borderRadius: 8,
                    border: "1px solid #eee",
                  }}
                />
              ) : null}
              <Div row gap="12px" alignStart wrap="wrap">
                <label
                  style={{
                    cursor: coverUploadDisabled || coverUploading
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  <P textWidth="auto" textAlign="left" fontWeight="600">
                    {coverUploading
                      ? "Uploading…"
                      : formData.coverUrl
                        ? "Replace cover"
                        : "Upload cover"}
                  </P>
                  <input
                    type="file"
                    disabled={coverUploadDisabled || coverUploading}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onCoverPick}
                    aria-label="Upload cover image file"
                    style={{ marginTop: 10 }}
                  />
                </label>
                {formData.coverUrl ? (
                  <Button
                    type="button"
                    outlineButton
                    width="auto"
                    fontSize="13px"
                    padding="5px 20px"
                    height="auto"
                    disabled={coverUploading}
                    onClick={onCoverClear}
                  >
                    Remove cover
                  </Button>
                ) : null}
              </Div>
              <FeedbackFormInput
                value={formData.coverUrl}
                onChange={(e) =>
                  handleFieldChange("coverUrl", null, e.target.value)
                }
                placeholder="https://firebasestorage.googleapis.com/..."
                aria-label="Cover image URL override"
                style={{ padding: "3px 5px", border: "1px gray solid" }}
              />
              <P margin="0" fontSize="12px" textAlign="left" style={{ color: "#666" }}>
                Upload resizes to max 1200px JPEG. Or paste a full HTTPS URL.
              </P>
              {coverUploadError ? (
                <P
                  margin="0"
                  fontSize="13px"
                  textAlign="left"
                  role="alert"
                  style={{ color: "#c62828" }}
                >
                  {coverUploadError}
                </P>
              ) : null}
            </Div>
            <Div flex="1" width="30%" alignStart justifyStart gap="10px">
              <P textWidth="auto" fontWeight="600">
                Published Date
              </P>
              <FeedbackFormInput
                type="date"
                value={formData.publishedAt}
                onChange={(e) =>
                  handleFieldChange("publishedAt", null, e.target.value)
                }
                required
                style={{ padding: "3px 5px", border: "1px gray solid" }}
              />
            </Div>
          </Div>

          <Div width="100%" gap="10px" alignStart style={{ marginTop: 10 }}>
            <P fontWeight="600" align="left" margin="0" textWidth="auto">
              Categories
            </P>
            <Div row gap="14px" wrap="wrap" justifyStart>
              {articleCategories.map((c) => (
                <label key={c.id}>
                  <input
                    type="checkbox"
                    checked={formData.categoryIds.includes(c.id)}
                    onChange={() => toggleCat(c.id)}
                  />
                  {" "}
                  {categoryLabel(c)}
                </label>
              ))}
              {!articleCategories.length && (
                <P margin="0" fontSize="13px" align="left" style={{ color: "#777" }}>
                  No categories yet — add them under Article categories admin.
                </P>
              )}
            </Div>
          </Div>

          <Div width="100%" gap="10px" alignStart>
            <P fontWeight="600" align="left" margin="0" textWidth="auto">
              Show in
            </P>
            <Div row gap="14px" wrap="wrap" justifyStart>
              <label>
                <input
                  type="checkbox"
                  checked={!formData.webOnly}
                  onChange={(e) => setChannel("mobile", e.target.checked)}
                />
                {" "}
                Mobile app
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={!formData.mobileOnly}
                  onChange={(e) => setChannel("webApp", e.target.checked)}
                />
                {" "}
                Web app
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.showOnSite)}
                  onChange={(e) => setChannel("site", e.target.checked)}
                />
                {" "}
                Site
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.pinned)}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, pinned: e.target.checked }))
                  }
                />
                {" "}
                Pin to top
              </label>
            </Div>
            <P margin="0" fontSize="12px" textAlign="left" style={{ color: "#666" }}>
              Mobile app and web app are the existing products. Site is
              blog.healthy-pet.app. Uncheck both apps for a site-only article.
              Pin to top applies to the apps.
            </P>
          </Div>

          <Div width="100%" gap="10px" alignStart>
            <P fontWeight="600" align="left" margin="0" textWidth="auto">
              Pet tags
            </P>
            <Div row gap="14px" wrap="wrap" justifyStart>
              {articlePetTags.map((t) => (
                <label key={t.id}>
                  <input
                    type="checkbox"
                    checked={formData.petTagIds.includes(t.id)}
                    onChange={() => togglePet(t.id)}
                  />
                  {" "}
                  {categoryLabel(t)}
                </label>
              ))}
              {!articlePetTags.length && (
                <P margin="0" fontSize="13px" align="left" style={{ color: "#777" }}>
                  No pet tags — add Article pet tags in admin.
                </P>
              )}
            </Div>
          </Div>

          <Div
            width="100%"
            row={isDesktop}
            gap={isDesktop ? "12px" : "15px"}
            alignStart
            justifyStart
            wrap={!isDesktop ? "wrap" : "nowrap"}
          >
            {LANGUAGES.map((lang) => (
              <Div
                key={lang}
                width={isDesktop ? undefined : "100%"}
                padding="15px"
                borderRadius="8px"
                style={{
                  backgroundColor: "#f5f5f5",
                  flex: isDesktop ? "1 1 0" : undefined,
                  minWidth: isDesktop ? 0 : undefined,
                  boxSizing: "border-box",
                }}
              >
                <P
                  fontSize="16px"
                  fontWeight="600"
                  margin="0 0 10px 0"
                  textAlign="left"
                >
                  {lang.toUpperCase()}
                </P>

                <Div gap="10px">
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Slug
                    </P>
                    <FeedbackFormInput
                      value={formData.slug[lang] || ""}
                      onChange={(e) =>
                        handleFieldChange("slug", lang, e.target.value)
                      }
                      placeholder={`slug-${lang}`}
                      style={{ padding: "3px 5px", border: "1px gray solid" }}
                    />
                  </Div>

                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Title
                    </P>
                    <FeedbackFormInput
                      value={formData.title[lang] || ""}
                      onChange={(e) =>
                        handleFieldChange("title", lang, e.target.value)
                      }
                      placeholder={`Title (${lang})`}
                      style={{ padding: "3px 5px", border: "1px gray solid" }}
                    />
                  </Div>

                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Subtitle
                    </P>
                    <FeedbackFormTextarea
                      as="textarea"
                      rows={4}
                      value={formData.subtitle[lang] || ""}
                      onChange={(e) =>
                        handleFieldChange("subtitle", lang, e.target.value)
                      }
                      placeholder={`Subtitle (${lang})`}
                      style={{
                        padding: "8px 5px",
                        border: "1px gray solid",
                        width: "100%",
                        boxSizing: "border-box",
                        resize: "vertical",
                      }}
                    />
                  </Div>
                </Div>
              </Div>
            ))}
          </Div>

          <AdminArticleCardsSection
            articleFirestoreId={articleFirestoreKey}
            cards={formData.cards?.length ? formData.cards : []}
            onChangeCards={(cards) =>
              setFormData((p) => ({ ...p, cards }))
            }
          />

          <AdminArticleSiteSection
            formData={formData}
            setFormData={setFormData}
            articleFirestoreKey={articleFirestoreKey}
            loadExisting={isEdit}
            onReadyChange={setSiteReady}
          />

          <Div row gap="10px" wrap="wrap" justifyStart>
            <Button type="submit" greenButton width="auto" disabled={busy || !siteReady}>
              {saving ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              width="auto"
              outlineButton
              disabled={busy}
            >
              Cancel
            </Button>
            {isEdit ? (
              <Button
                type="button"
                deleteButton
                width="auto"
                disabled={busy}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete article
              </Button>
            ) : null}
          </Div>
        </Div>
      </form>

      {isEdit ? (
        <CustomModal
          open={confirmDeleteOpen}
          onClose={() => !deleting && setConfirmDeleteOpen(false)}
          title="Delete article?"
        >
          <P style={{ marginBottom: 20 }}>
            This article will be permanently removed. This action cannot be undone.
          </P>
          <Div row gap="10px" justifyEnd>
            <Button
              type="button"
              temporaryButton
              fontWeight="400"
              width="auto"
              disabled={deleting}
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              deleteButton
              width="auto"
              disabled={deleting}
              onClick={handleDeleteConfirm}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </Div>
        </CustomModal>
      ) : null}
    </Div>
  );
}
